import moment from "moment";
import { EFILING_FILER_COMMENT_RESTRICTED_FIELDS } from "./efilingAccess";
import { getOfficeDocumentName } from "./businessEfilingOfficeDocuments";

export const BUSINESS_EFILING_TYPE_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "LLC", value: "LLC" },
  { id: "S-Corp", value: "S-Corp" },
  { id: "C-Corp", value: "C-Corp" },
  { id: "Non Profit", value: "Non Profit" },
];

export const BUSINESS_EFILING_PAYMENT_METHOD_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "Direct Deposit", value: "Direct Deposit" },
  // API stores "Check"; UI label is "Paper Check"
  { id: "Check", value: "Paper Check" },
  { id: "Online Payment", value: "Online Payment" },
  { id: "Direct Debit", value: "Direct Debit" },
];

export const BUSINESS_EFILING_INVOICE_STATUS_OPTIONS = [
  { id: "", value: "Select", keepAtTop: true },
  { id: "Pending", value: "Pending" },
  { id: "Paid", value: "Paid" },
  { id: "Partially Paid", value: "Partially Paid" },
];

function parseInvoiceAmount(raw) {
  const s = String(raw ?? "").replace(/[$,\s]/g, "");
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

function normalizePaymentMethod(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (s.toLowerCase() === "paper check") return "Check";
  return s;
}

function normalizeBusinessType(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const lower = s.toLowerCase().replace(/\s+/g, " ");
  if (lower === "s--corp" || lower === "s corp" || lower === "s corporation") {
    return "S-Corp";
  }
  if (lower === "c corp" || lower === "c corporation") return "C-Corp";
  if (
    lower === "non profit" ||
    lower === "nonprofit" ||
    lower === "non profit organization"
  ) {
    return "Non Profit";
  }
  if (lower.includes("llc")) return "LLC";
  return s;
}

export function emptyBusinessEfilingForm() {
  return {
    filing_date: moment().format("YYYY-MM-DD"),
    entity_name: "",
    business_type: "",
    state_filing: "",
    federal_due: "",
    fed_payment_method: "",
    state_due: "",
    state_payment_method: "",
    ca_estimate_payment: "",
    ca_payment_method: "",
    invoice_no: "",
    invoice_amount: "",
    invoice_status: "",
    filer_comment: "",
    signed_consents_link: "",
    password: "",
    other_comments: "",
    filing_status: "Pending",
    payment_status_readonly: "",
    payment_method_readonly: "",
    invoice_amount_readonly: "",
  };
}

export function recordToBusinessEditForm(record) {
  const r = record || {};
  const d = r.filing_date || r.date || r.created_at || r.createdAt || "";
  const filingDate =
    d && moment(d).isValid() ? moment(d).format("YYYY-MM-DD") : "";

  return {
    filing_date: filingDate || moment().format("YYYY-MM-DD"),
    entity_name: r.entity_name ?? r.entityName ?? "",
    business_type: normalizeBusinessType(
      r.business_type ?? r.businessType ?? r.type ?? "",
    ),
    state_filing: r.state_filing ?? r.stateFiling ?? "",
    federal_due: r.federal_due ?? r.federalDue ?? r.fed_due_ref ?? "",
    fed_payment_method: normalizePaymentMethod(
      r.fed_payment_method ?? r.fedPaymentMethod ?? "",
    ),
    state_due: r.state_due ?? r.stateDue ?? r.state_due_ref ?? "",
    state_payment_method: normalizePaymentMethod(
      r.state_payment_method ?? r.statePaymentMethod ?? "",
    ),
    ca_estimate_payment:
      r.ca_estimate_payment ?? r.caEstimatePayment ?? "",
    ca_payment_method: normalizePaymentMethod(
      r.ca_payment_method ?? r.caPaymentMethod ?? "",
    ),
    invoice_no: r.invoice_no ?? r.invoiceNo ?? "",
    invoice_amount:
      r.invoice_amount != null
        ? String(r.invoice_amount)
        : r.invoiceAmount != null
          ? String(r.invoiceAmount)
          : "",
    invoice_status:
      r.invoice_status ??
      r.invoiceStatus ??
      r.payment_status ??
      r.paymentStatus ??
      "",
    filer_comment: r.filer_comment ?? r.filerComment ?? "",
    signed_consents_link: r.signed_consents_link ?? r.signedConsentsLink ?? "",
    password: r.password ?? r.consent_password ?? r.consentPassword ?? "",
    other_comments: r.other_comments ?? r.otherComments ?? "",
    filing_status: r.filing_status ?? r.status ?? "Pending",
    payment_status_readonly: r.payment_status ?? r.paymentStatus ?? "",
    payment_method_readonly: r.payment_method ?? r.paymentMethod ?? "",
    invoice_amount_readonly:
      r.invoice_amount != null
        ? String(r.invoice_amount)
        : r.invoiceAmount != null
          ? String(r.invoiceAmount)
          : "",
  };
}

export function validateBusinessEfilingForm(form, options = {}) {
  const { skipStatusAndFilerComment = false } = options;
  const errors = {};
  const req = (key, val) => {
    if (!String(val ?? "").trim()) errors[key] = "This field is required";
  };

  req("filing_date", form.filing_date);
  req("entity_name", form.entity_name);
  req("business_type", form.business_type);
  req("state_filing", form.state_filing);
  req("federal_due", form.federal_due);
  req("fed_payment_method", form.fed_payment_method);
  req("state_due", form.state_due);
  req("state_payment_method", form.state_payment_method);
  req("ca_estimate_payment", form.ca_estimate_payment);
  req("ca_payment_method", form.ca_payment_method);
  req("invoice_no", form.invoice_no);
  req("invoice_amount", form.invoice_amount);
  req("invoice_status", form.invoice_status);
  if (String(form.invoice_amount ?? "").trim()) {
    const amount = parseInvoiceAmount(form.invoice_amount);
    if (amount == null) {
      errors.invoice_amount = "Enter a valid amount";
    }
  }
  req("filer_comment", form.filer_comment);
  if (!skipStatusAndFilerComment) {
    req("filing_status", form.filing_status);
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export function buildBusinessEfilingPayload(form, context, options = {}) {
  const {
    canEditFilerComment = false,
    preserveStatusAndFilerComment = false,
  } = options;
  const {
    clientId,
    preparerEmployeeId,
    preparerDisplay,
    officeDocument,
  } = context;

  const filingStatus = form.filing_status || "Pending";
  const otherComments = canEditFilerComment ? form.other_comments || null : null;

  const payload = {
    client_id: clientId,
    filing_date: form.filing_date,
    preparer_employee_id: preparerEmployeeId ?? null,
    preparer_display_name: preparerDisplay,
    entity_name: form.entity_name || null,
    business_type: form.business_type || null,
    state_filing: form.state_filing || null,
    federal_due: form.federal_due || null,
    fed_payment_method: form.fed_payment_method || null,
    state_due: form.state_due || null,
    state_payment_method: form.state_payment_method || null,
    ca_estimate_payment: form.ca_estimate_payment || null,
    ca_payment_method: form.ca_payment_method || null,
    invoice_no: officeDocument
      ? getOfficeDocumentName(officeDocument) || form.invoice_no || null
      : form.invoice_no || null,
    invoice_amount: parseInvoiceAmount(
      form.invoice_amount ?? form.invoice_amount_readonly,
    ),
    invoice_status: form.invoice_status || null,
    payment_status: form.invoice_status || form.payment_status_readonly || null,
    payment_method: form.payment_method_readonly || null,
    filer_comment: form.filer_comment || null,
    signed_consents_link: form.signed_consents_link || null,
    password: form.password || null,
  };

  if (!preserveStatusAndFilerComment) {
    payload.filing_status = filingStatus;
    payload.other_comments = otherComments;
  }

  return payload;
}

export function stripRestrictedBusinessEfilingFields(payload) {
  const next = { ...payload };
  EFILING_FILER_COMMENT_RESTRICTED_FIELDS.forEach((k) => delete next[k]);
  return next;
}

export function resolveEntityName(clientData) {
  if (!clientData) return "";
  return String(
    clientData.business_name ??
      clientData.businessName ??
      clientData.company_name ??
      clientData.companyName ??
      clientData.entity_name ??
      clientData.entityName ??
      "",
  ).trim();
}
